import { useTranslation } from 'react-i18next';

import { Button } from '../Button';
import { Input } from '../Input';
import { Select } from '../Select';
import { Tooltip } from '../Tooltip';
import { CloseIcon } from '../shell/icons';

import type {
  BulkAction,
  ColumnFilterDef,
  DataTableColumn,
} from './types';

interface Props<T> {
  columns: DataTableColumn<T>[];
  search?: string;
  onSearchChange?: (q: string) => void;
  searchPlaceholder?: string;
  filters?: Record<string, string>;
  onFilterChange?: (id: string, value: string) => void;
  selectedRows: T[];
  bulkActions?: BulkAction<T>[];
  hasPermission: (perm: string) => boolean;
  exportable?: boolean;
  onExport?: () => void;
  onClearSelection?: () => void;
}

export function Toolbar<T>({
  columns,
  search,
  onSearchChange,
  searchPlaceholder,
  filters = {},
  onFilterChange,
  selectedRows,
  bulkActions = [],
  hasPermission,
  exportable = false,
  onExport,
  onClearSelection,
}: Props<T>): JSX.Element | null {
  const { t } = useTranslation('common');
  const filterable = columns.filter((c): c is DataTableColumn<T> & { filter: ColumnFilterDef } =>
    Boolean(c.filter),
  );
  const hasSearch = onSearchChange !== undefined;
  const visibleBulks = bulkActions.filter((a) => !a.requires || hasPermission(a.requires));
  const showSelectionBar = selectedRows.length > 0 && visibleBulks.length > 0;
  if (!hasSearch && filterable.length === 0 && visibleBulks.length === 0 && !exportable) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2 border-b border-border px-3 py-2">
      <div className="flex flex-wrap items-center gap-2">
        {hasSearch ? (
          <Input
            type="search"
            value={search ?? ''}
            onChange={(e) => onSearchChange?.(e.target.value)}
            placeholder={searchPlaceholder ?? t('common.search')}
            className="h-8 max-w-xs"
            data-testid="datatable-search"
          />
        ) : null}
        {filterable.map((col) => (
          <FilterControl
            key={col.id}
            column={col}
            value={filters[col.id] ?? ''}
            onChange={(v) => onFilterChange?.(col.id, v)}
          />
        ))}
        {exportable && onExport ? (
          <Tooltip label={t('dataTable.export_tooltip')}>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={onExport}
              data-testid="datatable-export"
              className="ms-auto"
            >
              {t('dataTable.export')}
            </Button>
          </Tooltip>
        ) : null}
      </div>
      {showSelectionBar ? (
        <div
          role="region"
          aria-label={t('dataTable.bulk_region')}
          className="flex flex-wrap items-center gap-3 rounded-md border border-primary/30 bg-primary/5 px-3 py-2"
          data-testid="datatable-bulk-bar"
        >
          <span
            className="inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-md bg-primary px-1.5 text-xs font-semibold text-primary-foreground"
            aria-hidden="true"
          >
            {selectedRows.length}
          </span>
          <span className="text-xs font-medium text-foreground">
            {t('dataTable.selected_n', { n: selectedRows.length })}
          </span>
          <div className="flex flex-wrap items-center gap-2">
            {visibleBulks.map((action) => {
              const disabledReason = action.isDisabled?.(selectedRows) ?? null;
              return (
                <Button
                  key={action.id}
                  size="sm"
                  variant={action.variant ?? 'secondary'}
                  disabled={disabledReason !== null}
                  title={disabledReason ?? undefined}
                  onClick={() => void action.onRun(selectedRows)}
                  data-testid={`datatable-bulk-${action.id}`}
                >
                  {action.label}
                </Button>
              );
            })}
          </div>
          {onClearSelection ? (
            <button
              type="button"
              onClick={onClearSelection}
              aria-label={t('dataTable.clear_selection')}
              data-testid="datatable-bulk-clear"
              className="ms-auto inline-flex h-7 items-center gap-1 rounded-md px-2 text-xs text-muted-foreground hover:bg-background hover:text-foreground"
            >
              <CloseIcon size={12} />
              {t('dataTable.clear_selection')}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

interface FilterProps<T> {
  column: DataTableColumn<T> & { filter: ColumnFilterDef };
  value: string;
  onChange: (v: string) => void;
}

function FilterControl<T>({ column, value, onChange }: FilterProps<T>): JSX.Element {
  if (column.filter.type === 'select') {
    return (
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 max-w-[12rem]"
        data-testid={`datatable-filter-${column.id}`}
        aria-label={column.header}
      >
        {(column.filter.options ?? []).map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </Select>
    );
  }
  return (
    <Input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={column.filter.placeholder ?? column.header}
      className="h-8 max-w-xs"
      data-testid={`datatable-filter-${column.id}`}
      aria-label={column.header}
    />
  );
}
